import { ShowAllData } from '@/apiError/apiShow';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useSearchReport = ({
  setPagination,
  url,
  setTableData,
  startDate,
  endDate,
  ledgerType,
  subLedgerId,
  from,
  selectedFilters,
  setItemData,
  app,
  salesOrderId,
  searchParams,
  infinite = false,
  infinitePageSize = 20,
}) => {
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const generationRef = useRef(0);
  const [dataCache, setDataCache] = useState({});
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const navigate = useNavigate();

  // const dateToChoose =
  //   new Date(startDateT) > new Date(app?.financialEndYear)
  //     ? app?.financialEndYear
  //     : startDateT;

  // let startDate = dateToChoose;
  // let endDate = dateToChoose;

  const buildListUrl = (page) => {
    let urls = `${url}?startDate=${startDate}&endDate=${endDate}&pageNumber=${page}`;
    if (ledgerType) urls += `&ledgerType=${ledgerType}`;
    if (ledgerType && subLedgerId)
      urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&ledgerType=${ledgerType}`;
    if (from === 'sundry' && subLedgerId)
      urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&ledgerType=${'main'}`;
    if (from === 'stockValuation')
      urls = `${url}?startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&inventoryGroup=${selectedFilters}`;
    urls = salesOrderId ? `${urls}&salesOrderId=${salesOrderId}` : urls;
    Object.entries(searchParams || {}).forEach(([key, value]) => {
      if (value) urls += `&${key}=${encodeURIComponent(value)}`;
    });
    if (infinite) urls += `&pageSize=${infinitePageSize}`;
    return from === 'stock' ? url : urls;
  };

  const searchReport = async ({ page = 1, pageSize = 5 }) => {
    const searchKey = JSON.stringify(searchParams || {});
    const cacheKey = `${page}-${pageSize}-${searchKey}`;
    if (infinite) generationRef.current += 1;
    try {
      setLoading(true);
      if (!infinite && !isSearchClicked && dataCache[cacheKey]) {
        setTableData(dataCache[cacheKey]);
        return;
      }

      const response = await ShowAllData.showDetails(
        buildListUrl(infinite ? 1 : page),
        navigate,
      );
      if (infinite) setMetaData(response?.meta_data || null);

      const newData = response.data;
      setTableData(newData);
      from === 'stock' &&
        newData?.length &&
        setItemData &&
        setItemData((prev) => ({
          itemId: newData?.[0]?.itemId || prev?.itemId,
          itemName: newData?.[0]?.itemName || prev?.itemName,
          itemTypeName:
            newData?.[0]?.itemTypeName ||
            newData?.[0]?.typeName ||
            prev?.itemTypeName,
        }));
      setPagination &&
        setPagination({
          current: response.currentPage,
          pageSize: response?.pageSize,
          total: response?.totalCount,
        });
      setDataCache &&
        setDataCache((prevCache) => ({
          ...prevCache,
          [cacheKey]: newData,
        }));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!infinite || loading || loadingMoreRef.current) return;
    if (!metaData?.has_next) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const generation = generationRef.current;
    try {
      const response = await ShowAllData.showDetails(
        buildListUrl((metaData?.current_page || 1) + 1),
        navigate,
      );
      if (generation !== generationRef.current) return;
      setMetaData(response?.meta_data || null);
      setTableData((prev) => [...(prev || []), ...(response?.data || [])]);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
    searchReport({ page, pageSize });
  };

  const triggerSearch = () => {
    setIsSearchClicked(true);
    setDataCache({});
  };

  useEffect(() => {
    if (isSearchClicked) {
      searchReport({ page: 1, pageSize: 5 });
      setIsSearchClicked(false);
    }
  }, [isSearchClicked]);

  return {
    searchReport,
    handlePageChange,
    triggerSearch,
    loading,
    setIsSearchClicked,
    setLoading,
    loadMore,
    loadingMore,
    hasMore: !!metaData?.has_next,
    metaData,
  };
};
export const useWithoutPaginationSearchReport = ({
  url,
  setTableData,
  startDate,
  endDate,
  ledgerType,
  subLedgerId,
  from,
  selectedFilters,
  subGroupIds,
  groupData,
  agentData,
  itemData,
  ledgerData,
  clickableUrlFrom,
  selectedLedgers = '',
  purity = null,
  trayName = null,
  isDelete = 'all',
  isOrder = null,
  setMetaData,
  isPaginated = false,
  pageNumber = 1,
  pageSize = 20,
  storeIds = [],
  searchParams,
}) => {
  const [loading, setLoading] = useState(false);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const navigate = useNavigate();

  /**
   * Builds the report url for the current filters. The paging overrides let a
   * caller ask for a specific slice without changing what the table shows.
   *
   * @param {Object} params - Paging overrides
   * @param {number} [params.page] - Page number
   * @param {number} [params.pageSize] - Page size
   * @returns {string} Report url
   */
  const buildReportUrl = ({ page, pageSize: pageSizeOverride } = {}) => {
    let urls = `${url}?startDate=${startDate}&endDate=${endDate}`;
    if (ledgerType) urls += `&ledgerType=${ledgerType}`;
    if (ledgerType && subLedgerId)
      urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&ledgerType=${ledgerType}`;
    if (from === 'sundry' && subLedgerId)
      urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&ledgerType=${'main'}`;
    if (clickableUrlFrom === 'StockReport') {
      urls = `${url}?startDate=${startDate}&endDate=${endDate}`;
      if (selectedFilters?.length)
        urls += `&groupIds=${selectedFilters.join(',')}`;
      if (selectedLedgers?.length)
        urls += `&ledgerIds=${selectedLedgers.join(',')}`;
      if (isDelete) urls += `&status=${isDelete}`;
    } else if (from === 'stockValuation')
      // inventoryGroup;
      // urls = `${url}?startDate=${startDate}&endDate=${endDate}&inventoryGroup=${selectedFilters}&ledgerIds=${selectedLedgers}&purity=${purity}&trayName=${trayName}`;
      urls = `${url}?startDate=${startDate}&endDate=${endDate}&groupIds=${selectedFilters}&ledgerIds=${selectedLedgers}&purity=${purity}&trayName=${trayName}&status=${isDelete}&isOrder=${isOrder}&subGroupIds=${subGroupIds}`;
    if (from === 'detailReport') {
      urls = `${url}?startDate=${startDate}&endDate=${endDate}&groupIds=${groupData}&agentIds=${agentData}&itemIds=${itemData}&ledgerIds=${ledgerData}`;
    }

    if (isPaginated || page || pageSizeOverride) {
      urls += `&pageNumber=${page ?? pageNumber}&pageSize=${pageSizeOverride ?? pageSize}`;
    }

    if (storeIds?.length) {
      urls += `&storeId=${storeIds.join(',')}`;
    }

    if (selectedLedgers?.length && !urls.includes('ledgerIds=')) {
      urls += `&ledgerIds=${selectedLedgers.join(',')}`;
    }

    Object.entries(searchParams || {}).forEach(([key, value]) => {
      if (value) urls += `&${key}=${encodeURIComponent(value)}`;
    });

    return urls;
  };

  /**
   * Fetches report rows for the current filters and returns them, leaving the
   * rows on screen untouched. Exports use it to pull the full list.
   *
   * @param {Object} [params] - Paging overrides, see `buildReportUrl`
   * @returns {Promise<Object>} Report response
   */
  const fetchReportData = async (params = {}) =>
    ShowAllData.showDetails(buildReportUrl(params), navigate);

  const searchReport = async () => {
    try {
      setLoading(true);

      const response = await ShowAllData.showDetails(
        buildReportUrl(),
        navigate,
      );
      setMetaData && setMetaData(response.meta_data);
      setTableData(response.data);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = () => {
    searchReport();
  };

  const triggerSearch = () => {
    setIsSearchClicked(true);
  };

  useEffect(() => {
    if (isSearchClicked) {
      searchReport();
      setIsSearchClicked(false);
    }
  }, [isSearchClicked]);

  return {
    searchReport,
    fetchReportData,
    handlePageChange,
    triggerSearch,
    loading,
    setIsSearchClicked,
    setLoading,
  };
};

export const useColumnVisibility = (initialColumn) => {
  const [visibleColumns, setvisibleColumns] = useState(
    initialColumn.map((col) => col.referenceId),
  );
  const handleColumnVisibility = (columnKey, isVisible) => {
    setvisibleColumns((perv) =>
      isVisible
        ? [...perv, columnKey]
        : perv.filter((key) => key !== columnKey),
    );
  };
  const getFilteredColumns = () =>
    initialColumn.filter((col) => visibleColumns.includes(col.referenceId));
  return {
    visibleColumns,
    handleColumnVisibility,
    getFilteredColumns,
  };
};

export const useWithPaginationSearchReport = ({
  url,
  setTableData,
  startDate,
  endDate,
  ledgerType,
  subLedgerId,
  from,
  selectedFilters,
  groupData,
  agentData,
  itemData,
  ledgerData,
  clickableUrlFrom,
  selectedLedgers = '',
  purity = null,
  trayName = null,
  isDelete = 'all',
  isOrder = null,
  pageNumber,
  pageSize,
}) => {
  const [loading, setLoading] = useState(false);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const navigate = useNavigate();
  const searchReport = async () => {
    try {
      setLoading(true);

      let urls = `${url}?startDate=${startDate}&endDate=${endDate}&pageNumber=${pageNumber}`;
      if (ledgerType) urls += `&ledgerType=${ledgerType}`;
      if (ledgerType && subLedgerId)
        urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&ledgerType=${ledgerType}&pageNumber=${pageNumber}`;
      if (from === 'sundry' && subLedgerId)
        urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&ledgerType=${'main'}&pageNumber=${pageNumber}`;
      if (from === 'stockValuation' || clickableUrlFrom === 'StockReport')
        // inventoryGroup;
        // urls = `${url}?startDate=${startDate}&endDate=${endDate}&inventoryGroup=${selectedFilters}&ledgerIds=${selectedLedgers}&purity=${purity}&trayName=${trayName}`;
        urls = `${url}?startDate=${startDate}&endDate=${endDate}&groupIds=${selectedFilters}&ledgerIds=${selectedLedgers}&purity=${purity}&trayName=${trayName}&status=${isDelete}&isOrder=${isOrder}&pageNumber=${pageNumber}`;
      if (from === 'detailReport') {
        urls = `${url}?startDate=${startDate}&endDate=${endDate}&groupIds=${groupData}&agentIds=${agentData}&itemIds=${itemData}&ledgerIds=${ledgerData}&pageNumber=${pageNumber}`;
      }
      if (from === 'salesHistoryReport') {
        urls = `${url}?startDate=${startDate}&endDate=${endDate}&groupIds=${groupData}&agentIds=${agentData}&itemIds=${itemData}&ledgerIds=${ledgerData}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
      }

      const response = await ShowAllData.showDetails(urls, navigate);
      const newData = response.data;
      setTableData(newData);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = () => {
    searchReport();
  };

  const triggerSearch = () => {
    setIsSearchClicked(true);
  };

  useEffect(() => {
    if (isSearchClicked) {
      searchReport();
      setIsSearchClicked(false);
    }
  }, [isSearchClicked]);

  return {
    searchReport,
    handlePageChange,
    triggerSearch,
    loading,
    setIsSearchClicked,
    setLoading,
  };
};

export const useSearchReportWithPagination = ({
  setPagination,
  setPageNumber,
  setMetaData,
  setPageSize,
  url,
  setTableData,
  startDate,
  endDate,
  ledgerType,
  subLedgerId,
  from,
  selectedFilters,
  setItemData,
  app,
  salesOrderId,
  groupData,
  agentData,
  itemData,
  ledgerData,
  itemCode,
  storeIds = [],
  reportType,
  searchParams,
}) => {
  const [loading, setLoading] = useState(false);
  const [dataCache, setDataCache] = useState({});
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const navigate = useNavigate();

  /**
   * Builds the report url for the given page / page size / report type.
   *
   * @param {Object} params - Url params
   * @param {number} params.page - Page number
   * @param {number} params.pageSize - Page size
   * @param {string} params.type - Report type (`summary` | `detail`)
   * @returns {string} Report url
   */
  const buildReportUrl = ({ page = 1, pageSize = 5, type = reportType }) => {
    let urls = `${url}?startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&pageSize=${pageSize}`;
    if (ledgerType) urls += `&ledgerType=${ledgerType}`;
    if (ledgerType && subLedgerId)
      urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&ledgerType=${ledgerType}`;
    if (from === 'sundry' && subLedgerId)
      urls = `${url}?ledgerId=${subLedgerId}&startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&ledgerType=${'main'}`;
    if (from === 'stockValuation')
      urls = `${url}?startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&inventoryGroup=${selectedFilters}`;
    urls = salesOrderId ? `${urls}&salesOrderId=${salesOrderId}` : urls;
    if (from === 'detailReport') {
      urls = `${url}?startDate=${startDate}&endDate=${endDate}&pageNumber=${page}&pageSize=${pageSize}&groupIds=${groupData}&agentIds=${agentData}&itemIds=${itemData}&ledgerIds=${ledgerData}&reportType=${type}`;
    }
    if (itemCode) {
      urls += `&itemCodes=${encodeURIComponent(itemCode)}`;
    }
    if (storeIds?.length) {
      urls += `&storeId=${storeIds.join(',')}`;
    }
    Object.entries(searchParams || {}).forEach(([key, value]) => {
      if (value) urls += `&${key}=${encodeURIComponent(value)}`;
    });
    urls = salesOrderId ? `${urls}&salesOrderId=${salesOrderId}` : urls;

    return urls;
  };

  /**
   * @param {Object} params - Search params
   * @param {number} params.page - Page number
   * @param {number} params.pageSize - Page size
   * @param {string} params.type - Report type (`summary` | `detail`)
   * @param {boolean} params.append - Append to the current rows instead of
   *   replacing them, for scroll based pagination.
   */
  const searchReport = async ({
    page = 1,
    pageSize = 5,
    search,
    type = reportType,
    append = false,
  } = {}) => {
    const cacheKey = `${page}-${pageSize}`;
    try {
      if (!append) setLoading(true);
      // if (!isSearchClicked && dataCache[cacheKey]) {
      //   setTableData(dataCache[cacheKey]);
      //   return;
      // }
      const urls = buildReportUrl({ page, pageSize, type });
      // if (search) urls += `&search=${encodeURIComponent(search)}`;

      const response = await ShowAllData.showDetails(
        from === 'stock' ? url : urls,
        navigate,
      );

      setMetaData && setMetaData(response.meta_data);
      // setPageSize && setPageSize(response.meta_data?.page_size);
      setPageNumber && setPageNumber(response.meta_data?.current_page);

      const newData = response.data;
      setTableData(
        append ? (prev) => [...(prev || []), ...(newData || [])] : newData,
      );
      from === 'stock' &&
        newData?.length &&
        setItemData &&
        setItemData((prev) => ({
          itemId: newData?.[0]?.itemId || prev?.itemId,
          itemName: newData?.[0]?.itemName || prev?.itemName,
          itemTypeName:
            newData?.[0]?.itemTypeName ||
            newData?.[0]?.typeName ||
            prev?.itemTypeName,
        }));
      // setPagination &&
      //   setPagination({
      //     current: response.currentPage,
      //     pageSize: response?.pageSize,
      //     total: response?.totalCount,
      //   });
      // setDataCache &&
      //   setDataCache((prevCache) => ({
      //     ...prevCache,
      //     [`${page}-${pageSize}`]: newData,
      //   }));

      return response;
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber, pageSize) => {
    searchReport({ page: pageNumber, pageSize: pageSize });
  };

  const triggerSearch = () => {
    setIsSearchClicked(true);
    setDataCache({});
  };

  useEffect(() => {
    if (isSearchClicked) {
      searchReport({ page: 1, pageSize: 5 });
      setIsSearchClicked(false);
    }
  }, [isSearchClicked]);

  return {
    searchReport,
    handlePageChange,
    triggerSearch,
    loading,
    setIsSearchClicked,
    setLoading,
  };
};
